import { call, put, takeEvery, takeLatest } from "redux-saga/effects"

// Login Redux States
import {
  LOGIN_USER,
  LOGOUT_USER,
  SOCIAL_LOGIN,
  ACTIVATE_ACCOUNT,
  GUEST_LOGIN,
} from "./actionTypes"
import { apiError, loginSuccess, logoutUserSuccess } from "./actions"

//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../../helpers/firebase_helper"
import {
  postFakeLogin,
  postJwtLogin,
  postSocialLogin,
} from "../../../helpers/fakebackend_helper"

import { api} from "../../../utils/variables"
import axios from "axios"
import { removeLocalItem, getOrCreateDeviceId } from "../../../utils/storage"
import { analytics, ANALYTICS_EVENTS } from "../../../analytics"

const fireBaseBackend = getFirebaseBackend()

// Function to handle the API login request
function loginApi(user) {
  try {
    const res = axios.post(`${api}/user/login`, {
      email: user.email,
      password: user.password,
    })
    return res
  } catch (error) { }
}

// function activateAccount(user) {
//   try {
//     const res = axios.post(`${api}/user/activate`, {
//       email: user.email,
//     })
//     console.log(res)
//   } catch (error) {
//     console.log(error)
//   }
// }
function activateAccountApi(email) {
  return axios.post(`${api}/user/activate`, { email: email })
}

function guestLoginApi() {
  const deviceId = getOrCreateDeviceId()
  return axios.post(`${api}/user/guest-login`, { device_id: deviceId })
}

function* loginUser({ payload: { user, history, redirectTo, initialPrompt } }) {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(
        fireBaseBackend.loginUser,
        user.email,
        user.password
      )
      yield put(loginSuccess(response))
    } else if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      const response = yield call(postJwtLogin, {
        email: user.email,
        password: user.password,
      })
      localStorage.setItem("authUser", JSON.stringify(response))
      yield put(loginSuccess(response))
    } else if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
      const response = yield call(loginApi, {
        email: user.email,
        password: user.password,
      })
      // If login is successful, store the access token
      const accessToken = response?.data?.access_token || "NA"
      const userName = user.email.split("@")[0]
      localStorage.setItem("authToken", accessToken)
      localStorage.setItem("userName", userName)
      localStorage.setItem("userEmail", user.email)
      localStorage.setItem("authUser", JSON.stringify(response))
      yield put(loginSuccess(response))
    }

    analytics.setUser(getOrCreateDeviceId())
    analytics.trackEvent(ANALYTICS_EVENTS.AUTH.LOGIN, { method: process.env.REACT_APP_DEFAULTAUTH })

    // If an initial prompt was requested, clear the firstLoginPrompt flag now that login succeeded
    if (initialPrompt) {
      try { localStorage.removeItem('firstLoginPrompt') } catch (e) { /* ignore */ }
      try { localStorage.removeItem('firstLoginPromptEmail') } catch (e) { /* ignore */ }
    }

    // Navigate to requested redirect path (with optional state) or default dashboard
    if (redirectTo) {
      try {
        if (initialPrompt) history(redirectTo, { state: { initialPrompt } })
        else history(redirectTo)
      } catch (err) {
        // Some callers may pass a simple navigate function; ensure fallback
        history(redirectTo)
      }
    } else {
      history("/dashboard")
    }
  } catch (error) {
    console.log(error)
    if (error.response && error.response.status === 400) {
      yield put(apiError(error.response.data.error))
    } else {
      // Handle other errors
      yield put(apiError(error))
    }
  }
}

function* guestLoginSaga({ payload: { history, redirectTo } }) {
  try {
    const response = yield call(guestLoginApi)
    const accessToken = response?.data?.access_token || "NA"
    const userEmail = response?.data?.email || "guest@trivium.in"
    const userName = response?.data?.email ? response.data.email.split("@")[0] : "Guest"
    
    // Store variables normally as a real user
    localStorage.setItem("authToken", accessToken)
    localStorage.setItem("userName", userName)
    localStorage.setItem("userEmail", userEmail)
    localStorage.setItem("authUser", JSON.stringify(response))

    yield put(loginSuccess(response))

    analytics.setUser(getOrCreateDeviceId())
    analytics.trackEvent(ANALYTICS_EVENTS.AUTH.GUEST_LOGIN)

    // Navigate to requested redirect path (with optional state) or default dashboard
    if (redirectTo) {
      try {
        history(redirectTo)
      } catch (err) {
        history(redirectTo)
      }
    } else {
      history("/dashboard")
    }
  } catch (error) {
    console.log(error)
    if (error.response && error.response.data && error.response.data.error) {
      yield put(apiError(error.response.data.error))
    } else {
      yield put(apiError("Guest login failed"))
    }
  }
}

function* activateAccountSaga({ payload }) {
  try {
    yield call(activateAccountApi, payload.email)
    // Dispatch success message
    yield put(apiError("Activation link sent to your mail")) // Ensure success message
  } catch (error) {
    yield put(apiError("Activation failed. Please try again later."))
  }
}

function* logoutUser({ payload: { history } }) {
  try {
    analytics.trackEvent(ANALYTICS_EVENTS.AUTH.LOGOUT)
    analytics.reset()

    // Note: We DON'T clear chat history on logout - it persists per user via namespaced storage
    // Each user's chats are stored with their user ID, so they'll see their chats when they log back in
    // Chat history is only cleared when a DIFFERENT user logs in (handled by ChatHistoryContext)

    // Clear auth-related storage
    try { localStorage.removeItem("authUser") } catch (e) { }
    try { localStorage.removeItem("authToken") } catch (e) { }
    try { localStorage.removeItem("userName") } catch (e) { }
    try { localStorage.removeItem("userEmail") } catch (e) { }

    // Clear chat and first-login flags so UI won't re-open chatbot on next navigation
    try { localStorage.removeItem("chatMessages") } catch (e) { }
    try { localStorage.removeItem("firstLoginPrompt") } catch (e) { }
    try { localStorage.removeItem("firstLoginPromptEmail") } catch (e) { }

    // Clear transient sessionStorage keys used for toasts
    try { sessionStorage.removeItem('registrationSuccess') } catch (e) { }
    try { sessionStorage.removeItem('verificationSuccess') } catch (e) { }
    try { sessionStorage.removeItem('verificationError') } catch (e) { }

    // Remove any chatbot UI body classes and modal backdrops that could persist across routes
    try {
      if (typeof document !== 'undefined' && document?.body?.classList) {
        document.body.classList.remove('chatbot-initial-prompt')
        document.body.classList.remove('modal-open')
      }
      if (typeof document !== 'undefined') {
        const backdrops = Array.from(document.querySelectorAll('.modal-backdrop'))
        backdrops.forEach(b => b.parentNode && b.parentNode.removeChild(b))
      }
    } catch (e) { }

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(fireBaseBackend.logout)
      // dispatch success for firebase flows
      yield put(logoutUserSuccess(response))
    } else {
      // ensure reducers receive logout success for non-firebase flows too
      yield put(logoutUserSuccess())
    }

    // Final navigation to landing page (replace current entry to clear any navigation state)
    try {
      history("/", { replace: true, state: {} })
    } catch (e) {
      history("/")
    }
  } catch (error) {
    yield put(apiError(error))
  }
}

function* socialLogin({ payload: { data, history, type } }) {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend = getFirebaseBackend()
      const response = yield call(fireBaseBackend.socialLoginUser, data, type)
      localStorage.setItem("authUser", JSON.stringify(response))
      yield put(loginSuccess(response))
    } else {
      const response = yield call(postSocialLogin, data)

      localStorage.setItem("authUser", JSON.stringify(response))
      yield put(loginSuccess(response))
    }
    history("/dashboard")
  } catch (error) {
    yield put(apiError(error))
  }
}

function* authSaga() {
  yield takeLatest(ACTIVATE_ACCOUNT, activateAccountSaga)
  yield takeEvery(LOGIN_USER, loginUser)
  yield takeLatest(SOCIAL_LOGIN, socialLogin)
  yield takeEvery(LOGOUT_USER, logoutUser)
  yield takeEvery(GUEST_LOGIN, guestLoginSaga)
}

export default authSaga
